import { entries } from '@debbie/common/dist/util/index.js';
import moment from 'moment';
import React from 'react';
import { useSelector } from 'react-redux';
import ReactSelect from 'react-select';
import { styled } from 'styled-components';
import THEME from '../../theme.js';
import CheckboxSimple from '../CheckboxSimple.js';
import DateInput from '../DateInput.js';
import Input from '../Input.js';
import MoneyInput from '../MoneyInput.js';
import Select from '../Select.js';
import { useFilterSearch } from './FilterSearch.js';
export const DateRangeFilter = (props) => {
    const lang = useSelector((s) => s.lang);
    return (React.createElement("div", { style: { display: 'flex', flexDirection: 'column' } },
        React.createElement(DateInput, { allowNull: true, value: props.from != null && props.from !== '' ? moment(props.from) : null, onChange: (x) => {
                if (x) {
                    props.onChange(x.clone().startOf('day').toISOString(), props.to);
                }
                else {
                    props.onChange(x, props.to);
                }
            }, placeholder: lang.table.from }),
        React.createElement(DateInput, { allowNull: true, value: props.to != null && props.to !== '' ? moment(props.to) : null, onChange: (x) => {
                if (x) {
                    // The end of the day, so the chosen day itself is included
                    props.onChange(props.from, x.clone().endOf('day').toISOString());
                }
                else {
                    props.onChange(props.from, x);
                }
            }, placeholder: lang.table.to })));
};
export const NumberRangeFilter = (props) => {
    const lang = useSelector((s) => s.lang);
    return (React.createElement("div", null,
        React.createElement(Input, { type: "number", placeholder: lang.table.from, value: props.from ?? undefined, onChange: (e) => {
                const parsedNum = parseInt(e.currentTarget.value);
                if (isNaN(parsedNum)) {
                    props.onChange(null, props.to);
                    return;
                }
                props.onChange(parsedNum, props.to);
            } }),
        React.createElement(Input, { type: "number", placeholder: lang.table.to, value: props.to ?? undefined, onChange: (e) => {
                const parsedNum = parseInt(e.currentTarget.value);
                if (isNaN(parsedNum)) {
                    props.onChange(props.from, null);
                    return;
                }
                props.onChange(props.from, parsedNum);
            } })));
};
export const SearchFilter = (props) => {
    const lang = useSelector((s) => s.lang);
    return (React.createElement("div", null,
        React.createElement(Input, { type: "text", onChange: (e) => {
                if (e.currentTarget.value != null &&
                    e.currentTarget.value.length > 0) {
                    props.onChange(e.currentTarget.value);
                }
                else {
                    props.onChange(null);
                }
            }, placeholder: lang.table.search, value: props.query ?? '' })));
};
export const MoneyRangeFilter = (props) => {
    const lang = useSelector((s) => s.lang);
    return (React.createElement("div", null,
        React.createElement(MoneyInput, { allowNull: true, value: props.from, placeholder: lang.table.from, onChange: (x) => {
                props.onChange(x, props.to);
            } }),
        React.createElement(MoneyInput, { allowNull: true, value: props.to, placeholder: lang.table.to, onChange: (x) => {
                props.onChange(props.from, x);
            } })));
};
export const CategoryFilter = (props) => {
    const lang = useSelector((s) => s.lang);
    const [search, setSearch] = useFilterSearch();
    const options = entries(props.categories).filter(([, category]) => category != null);
    const searchable = options.length > 3;
    const categories = options.filter(([, category]) => category != null &&
        (!searchable ||
            typeof category.label !== 'string' ||
            category.label
                .toLocaleLowerCase()
                .includes(search.trim().toLocaleLowerCase())));
    return (React.createElement("div", { "data-searchable-category-filter": searchable },
        searchable && (React.createElement(CategorySearch, { type: "search", placeholder: lang.table.search, aria: { 'aria-label': lang.table.search }, value: search, onChange: (event) => setSearch(event.target.value) })),
        React.createElement(CategoryList, { $bounded: searchable },
            categories.map(([key, val]) => {
                if (val == null) {
                    return null;
                }
                const checked = props.selected.includes(key);
                const toggle = () => props.onChange(checked
                    ? props.selected.filter((selected) => selected !== key)
                    : [...props.selected, key]);
                return (React.createElement(CategoryItem, { key: key, "data-filter-focus": `option:${key}`, onClick: toggle },
                    React.createElement(CategoryCheckbox, { checked: checked, ariaLabel: typeof val.label === 'string' ? val.label : undefined, onChange: toggle, onClick: (e) => e.stopPropagation() }),
                    React.createElement(CategoryItemText, { $wrap: searchable }, val.label)));
            }),
            searchable && categories.length === 0 && (React.createElement(CategoryEmpty, { role: "status" }, lang.search.noResults))),
        searchable && (React.createElement(CategoryFooter, null,
            React.createElement("span", null,
                lang.table.selectedOptions,
                ": ",
                props.selected.length),
            React.createElement(CategoryClear, { "data-filter-focus": "clear", type: "button", disabled: props.selected.length === 0, onClick: () => props.onChange([]) }, lang.inputs.clear)))));
};
const CategorySearch = styled(Input) `
  margin-bottom: 10px;
  font-size: 13px;
`;
const CategoryList = styled.div `
  max-height: ${(props) => (props.$bounded ? 'min(320px, 45vh)' : 'none')};
  overflow-y: auto;
  overscroll-behavior: contain;
`;
const CategoryCheckbox = styled(CheckboxSimple) `
  padding: 6px;
  flex-shrink: 0;
`;
const CategoryEmpty = styled.div `
  padding: 20px 8px;
  font-size: 13px;
  color: ${THEME.COLOR_GREY};
`;
const CategoryFooter = styled.div `
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 6px 2px;
  margin-top: 8px;
  border-top: 1px solid #e5e6ef;
  font-size: 12px;
`;
const CategoryClear = styled.button `
  border: 0;
  background: transparent;
  color: ${THEME.COLOR_ACCENT};
  font: inherit;
  cursor: pointer;
  &:disabled {
    opacity: 0.4;
    cursor: default;
  }
`;
const CategoryItem = styled.div `
  display: flex;
  align-items: center;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 50ms ease-in-out;

  &:hover {
    background-color: color-mix(in hsl, ${THEME.COLOR_GREY}, transparent 80%);
  }
`;
const CategoryItemText = styled.div `
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: ${(props) => (props.$wrap ? 'normal' : 'nowrap')};
  overflow-wrap: anywhere;
  padding: 6px;
`;
export const BooleanFilter = (props) => {
    const lang = useSelector((s) => s.lang);
    return (React.createElement("div", null,
        React.createElement(Select, { value: props.filter.showTrue ?? 'all', onChange: (e) => {
                const value = e.currentTarget.value;
                if (value == 'all') {
                    props.onChange({
                        showTrue: undefined,
                    });
                }
                else if (value === 'true') {
                    props.onChange({
                        showTrue: true,
                    });
                }
                else if (value === 'false') {
                    props.onChange({
                        showTrue: false,
                    });
                }
            } },
            React.createElement("option", { value: 'all' }, lang.misc.all),
            React.createElement("option", { value: 'true' }, lang.misc.yes),
            React.createElement("option", { value: 'false' }, lang.misc.no))));
};
export const SearchCategoryFilter = (props) => {
    const lang = useSelector((s) => s.lang);
    return (React.createElement("div", null,
        React.createElement(StyledReactSelect, { isMulti: true, placeholder: props.placeholder ?? lang.table.search, classNamePrefix: "select", value: props.options.filter((o) => props.selected.includes(o.value)), options: props.options, onChange: (val) => {
                const selected = val != null ? val.map((v) => v.value) : [];
                props.onChange(selected);
            } })));
};
const StyledReactSelect = styled(ReactSelect) `
  & .select__menu {
    z-index: 11;
  }
`;
//# sourceMappingURL=Filters.js.map